import React, { useMemo, useState, useEffect } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Activity, Users, Home, Loader2, ExternalLink, ArrowUpRight, Save, Download, Send, Plug, Check } from "lucide-react";

/**
 * Phase 3 — Client Portal Dashboard + Quick Status Board.
 *
 * Sales-focused landing view. No technical scene/transition/zone tools —
 * just inventory status, lead pulse, and a quick way to edit unit statuses
 * (with audit trail via the existing client.entities PUT endpoint).
 */

export function ClientDashboardView({ project, entities, leads, analytics, onJumpView }) {
  // iter50 — CRM status chip. Connection state comes from /client/crm-config;
  // "synced today" is derived from the already-loaded leads.
  const [crm, setCrm] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/client/crm-config").then((r) => { if (alive) setCrm(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const syncedToday = useMemo(() => {
    const now = new Date();
    return (leads || []).filter((l) => {
      if (!l.crm_pushed || !l.crm_pushed_at) return false;
      const d = new Date(l.crm_pushed_at);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
    }).length;
  }, [leads]);
  const crmConnected = !!(crm && crm.provider && crm.api_token_set);
  const crmProviderLabel = crm?.provider === "pipedrive" ? "Pipedrive" : crm?.provider === "hubspot" ? "HubSpot" : "";

  // Inventory KPIs
  const totals = useMemo(() => {
    const byStatus = entities.reduce((acc, e) => {
      const s = e.status || "available";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    return {
      total: entities.length,
      available: byStatus.available || 0,
      reserved: byStatus.reserved || 0,
      sold: byStatus.sold || 0,
      hold: byStatus.on_hold || byStatus.hold || 0,
    };
  }, [entities]);

  // Recent leads (last 14 days)
  const recentLeads = useMemo(() => {
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return (leads || [])
      .filter((l) => new Date(l.created_at || 0).getTime() > cutoff)
      .slice(0, 6);
  }, [leads]);

  // Top interested units — leads grouped by interested_unit_codes
  const topUnits = useMemo(() => {
    const counts = new Map();
    for (const l of leads || []) {
      const codes = l.interested_unit_codes || [];
      for (const c of codes) counts.set(c, (counts.get(c) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([code, n]) => {
        const unit = entities.find((e) => e.code === code);
        return { code, count: n, unit };
      });
  }, [leads, entities]);

  // Recent activity feed: combine last lead.created_at + last entity.updated_at
  const recentActivity = useMemo(() => {
    const events = [];
    for (const l of leads || []) {
      events.push({ kind: "lead", ts: l.created_at, label: `New lead: ${l.name || l.phone || "buyer"}`, sub: (l.interested_unit_codes || []).slice(0, 2).join(", ") });
    }
    for (const e of entities) {
      if (e.updated_at) events.push({ kind: "unit", ts: e.updated_at, label: `${e.code} → ${e.status}`, sub: e.price ? `${e.price.toLocaleString()}` : "" });
    }
    return events.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0)).slice(0, 8);
  }, [leads, entities]);

  return (
    <div className="space-y-6" data-testid="client-dashboard-view">
      {/* iter50 — CRM status chip */}
      <button
        onClick={() => onJumpView("leads")}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition ${
          crmConnected
            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            : "border-zinc-200 bg-zinc-50 text-zinc-500 hover:bg-zinc-100"
        }`}
        data-testid="dash-crm-chip"
        title={crmConnected ? "Manage CRM in the Leads tab" : "Connect a CRM in the Leads tab"}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${crmConnected ? "bg-emerald-500" : "bg-zinc-300"}`} />
        {crmConnected ? (
          <>
            <Check className="h-3 w-3" />
            CRM connected · {crmProviderLabel}
            <span className="text-emerald-500">·</span>
            {syncedToday} synced today
          </>
        ) : (
          <>
            <Plug className="h-3 w-3" />
            CRM not connected — click to set up
          </>
        )}
      </button>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total units" value={totals.total} testid="dash-kpi-total" />
        <KpiCard label="Available" value={totals.available} accent="emerald" testid="dash-kpi-available" />
        <KpiCard label="Reserved" value={totals.reserved} accent="amber" testid="dash-kpi-reserved" />
        <KpiCard label="Sold" value={totals.sold} accent="zinc" testid="dash-kpi-sold" />
        <KpiCard label="Leads" value={leads.length} testid="dash-kpi-leads" subline={`${recentLeads.length} new in 14d`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top interested units */}
        <div className="lg:col-span-2 rounded-xl border border-zinc-200 bg-white p-4" data-testid="dash-top-units">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-zinc-900 inline-flex items-center gap-2"><Home className="h-4 w-4 text-zinc-500" /> Top interested units</h3>
            <button onClick={() => onJumpView("status")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="dash-jump-status">
              Status board <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
          {topUnits.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">No buyers have shortlisted units yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="dash-top-units-list">
              {topUnits.map(({ code, count, unit }) => (
                <li key={code} className="flex items-center gap-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-900 font-medium">{code}{unit?.name && unit.name !== code ? ` — ${unit.name}` : ""}</div>
                    <div className="text-xs text-zinc-500">
                      {unit ? `${(unit.status || "—").replace(/_/g, " ")}${unit.price ? ` · ${unit.price.toLocaleString()}` : ""}` : "Unit no longer exists"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-light tabular-nums">{count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">interest</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent activity */}
        <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="dash-activity">
          <h3 className="text-sm font-medium text-zinc-900 mb-3 inline-flex items-center gap-2"><Activity className="h-4 w-4 text-zinc-500" /> Recent activity</h3>
          {recentActivity.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">Nothing yet.</div>
          ) : (
            <ul className="space-y-2" data-testid="dash-activity-list">
              {recentActivity.map((e, i) => (
                <li key={i} className="text-xs text-zinc-700 border-l-2 border-zinc-200 pl-2.5 py-0.5">
                  <div className="font-medium text-zinc-900">{e.label}</div>
                  {e.sub && <div className="text-zinc-500">{e.sub}</div>}
                  {e.ts && <div className="text-zinc-400 mt-0.5">{new Date(e.ts).toLocaleString()}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent leads */}
      <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="dash-recent-leads">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-zinc-900 inline-flex items-center gap-2"><Users className="h-4 w-4 text-zinc-500" /> Recent leads</h3>
          <button onClick={() => onJumpView("units")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="dash-jump-leads">
            All units <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>
        {recentLeads.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No buyers in the last 14 days.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {recentLeads.map((l) => (
              <div key={l.id} className="rounded-lg border border-zinc-100 px-3 py-2 text-sm" data-testid={`dash-lead-${l.id}`}>
                <div className="font-medium text-zinc-900 truncate">{l.name || l.phone || "Anonymous"}</div>
                <div className="text-xs text-zinc-500 truncate">
                  {l.phone || ""}{l.email ? ` · ${l.email}` : ""}
                </div>
                {(l.interested_unit_codes || []).length > 0 && (
                  <div className="text-[11px] text-zinc-600 mt-1">Interested: {l.interested_unit_codes.join(", ")}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, subline, accent, testid }) {
  const cls = accent === "emerald" ? "border-emerald-200 bg-emerald-50/30" :
              accent === "amber" ? "border-amber-200 bg-amber-50/30" :
              accent === "zinc" ? "border-zinc-300 bg-zinc-50" : "border-zinc-200 bg-white";
  return (
    <div className={`rounded-xl border px-4 py-3 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="font-display text-3xl mt-1 tabular-nums text-zinc-900">{value}</div>
      {subline && <div className="text-[11px] text-zinc-500 mt-1">{subline}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Quick Status Board — inline status + price edits grouped by Etap/Block.
 * Saves go to the existing PUT /api/client/entities/{eid} (audit-logged).
 * ----------------------------------------------------------------------- */

const STATUSES = ["available", "reserved", "sold", "on_hold"];

export function ClientStatusBoardView({ entities, groups, onReload }) {
  // Track per-row dirty state
  const [dirty, setDirty] = useState({});  // {eid: {status?, price?}}
  const [savingId, setSavingId] = useState(null);

  const etaps = useMemo(() =>
    groups.filter((g) => g.group_type === "etap").sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
  [groups]);
  const blocks = useMemo(() =>
    groups.filter((g) => g.group_type === "block").sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
  [groups]);

  // Group entities by etap → block. Units whose block has no parent_group_id
  // (legacy/no etap) are dropped into an "Other" etap bucket.
  const grouped = useMemo(() => {
    const blockById = Object.fromEntries(blocks.map((b) => [b.id, b]));
    const buckets = new Map(); // etapKey → {etap, blockMap}
    const ensureEtap = (etap) => {
      const key = etap?.id || "__other__";
      if (!buckets.has(key)) buckets.set(key, { etap, blockMap: new Map() });
      return buckets.get(key);
    };
    for (const e of entities) {
      const block = blockById[e.group_id];
      const etap = etaps.find((g) => g.id === block?.parent_group_id);
      const slot = ensureEtap(etap);
      const blockKey = block?.id || "__no_block__";
      if (!slot.blockMap.has(blockKey)) slot.blockMap.set(blockKey, { block, items: [] });
      slot.blockMap.get(blockKey).items.push(e);
    }
    return [...buckets.values()]
      .sort((a, b) => (a.etap?.sort_order || 0) - (b.etap?.sort_order || 0))
      .map(({ etap, blockMap }) => ({
        etap,
        blocks: [...blockMap.values()]
          .sort((a, b) => (a.block?.sort_order || 0) - (b.block?.sort_order || 0))
          .map(({ block, items }) => ({
            block,
            items: items.slice().sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true })),
          })),
      }));
  }, [entities, groups, etaps, blocks]);

  const setField = (eid, key, value) => {
    setDirty((d) => ({ ...d, [eid]: { ...d[eid], [key]: value } }));
  };

  const save = async (entity) => {
    const patch = dirty[entity.id];
    if (!patch) return;
    setSavingId(entity.id);
    try {
      await api.put(`/client/entities/${entity.id}`, patch);
      setDirty((d) => { const n = { ...d }; delete n[entity.id]; return n; });
      toast.success(`${entity.code} updated`);
      onReload?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingId(null);
    }
  };

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="space-y-4" data-testid="client-status-board">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-medium text-zinc-900">Quick status board</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Update status and price inline — changes save individually and are written to the audit log.</p>
        </div>
        <div className="text-xs text-zinc-500" data-testid="status-board-summary">
          {entities.length} units · {dirtyCount > 0 && <span className="text-amber-700 font-medium">{dirtyCount} unsaved</span>}
        </div>
      </div>

      {grouped.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500">No units yet.</div>
      ) : (
        grouped.map(({ etap, blocks: groupedBlocks }) => (
          <div key={etap?.id || "other"} className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={`status-board-etap-${etap?.code || etap?.id || "other"}`}>
            <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 text-xs uppercase tracking-[0.2em] text-zinc-600">
              {etap ? etap.name : "Other"}
            </div>
            {groupedBlocks.map(({ block, items }) => (
              <div key={block?.id || "no-block"} className="border-b border-zinc-100 last:border-b-0">
                <div className="px-4 py-1.5 text-[11px] text-zinc-500 font-medium">{block ? block.name : "—"}</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                      <th className="text-left px-4 py-1.5 font-normal w-24">Unit</th>
                      <th className="text-left px-3 py-1.5 font-normal">Name</th>
                      <th className="text-left px-3 py-1.5 font-normal w-36">Status</th>
                      <th className="text-left px-3 py-1.5 font-normal w-32">Price</th>
                      <th className="text-right px-4 py-1.5 font-normal w-24">Updated</th>
                      <th className="text-right px-3 py-1.5 font-normal w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((e) => {
                      const patch = dirty[e.id] || {};
                      const status = patch.status ?? e.status ?? "available";
                      const price = patch.price ?? (e.price ?? "");
                      const hasChange = !!Object.keys(patch).length;
                      return (
                        <tr key={e.id} className={`border-t border-zinc-100 ${hasChange ? "bg-amber-50/30" : ""}`} data-testid={`status-row-${e.code}`}>
                          <td className="px-4 py-1.5 font-medium text-zinc-900">{e.code}</td>
                          <td className="px-3 py-1.5 text-zinc-700 truncate max-w-xs">{e.name}</td>
                          <td className="px-3 py-1.5">
                            <select
                              value={status}
                              onChange={(ev) => setField(e.id, "status", ev.target.value)}
                              className="w-full px-2 py-1 text-xs rounded border border-zinc-300 bg-white"
                              data-testid={`status-select-${e.code}`}
                            >
                              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-1.5">
                            <input
                              type="number"
                              value={price}
                              onChange={(ev) => setField(e.id, "price", ev.target.value === "" ? null : parseFloat(ev.target.value))}
                              className="w-full px-2 py-1 text-xs rounded border border-zinc-300 bg-white tabular-nums"
                              data-testid={`status-price-${e.code}`}
                            />
                          </td>
                          <td className="px-4 py-1.5 text-[11px] text-zinc-500 text-right">
                            {e.updated_at ? new Date(e.updated_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            {hasChange && (
                              <button
                                onClick={() => save(e)}
                                disabled={savingId === e.id}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-zinc-900 text-white text-xs hover:bg-zinc-800 disabled:opacity-50"
                                data-testid={`status-save-${e.code}`}
                              >
                                {savingId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                Save
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}


// ── Client Leads view (iter46) ────────────────────────────────────────────
// Full lead detail for the client — every field the buyer left plus the
// journey context (interested/shortlisted units + engagement signals). The
// backend already returns the complete lead documents scoped to the client's
// own project; this simply surfaces them.
export function ClientLeadsView({ leads, entities, pid }) {
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [crm, setCrm] = useState(null);
  const [showCrm, setShowCrm] = useState(false);
  const [pushingId, setPushingId] = useState(null);
  const [pushingAll, setPushingAll] = useState(false);
  const codeFor = (id) => entities.find((e) => e.id === id)?.code || null;
  const sorted = useMemo(
    () => [...(leads || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [leads],
  );

  const loadCrm = async () => {
    try { const r = await api.get("/client/crm-config"); setCrm(r.data); } catch { /* ignore */ }
  };
  useEffect(() => { loadCrm(); /* eslint-disable-next-line */ }, []);
  const crmReady = !!(crm && crm.provider && crm.api_token_set);

  const pushOne = async (leadId) => {
    setPushingId(leadId);
    try {
      const r = await api.post(`/client/projects/${pid}/leads/${leadId}/push-crm`);
      if (r.data?.ok) toast.success(r.data.detail || "Sent to CRM");
      else toast.error(r.data?.detail || "CRM push failed");
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setPushingId(null); }
  };

  const pushAll = async () => {
    setPushingAll(true);
    try {
      const r = await api.post(`/client/projects/${pid}/leads/push-crm`);
      const { total, pushed, failed, errors } = r.data || {};
      if (!total) toast.info("All leads already sent to CRM");
      else if (!failed) toast.success(`Sent ${pushed} lead${pushed === 1 ? "" : "s"} to CRM`);
      else toast.warning(`Sent ${pushed}, ${failed} failed. ${(errors || [])[0] || ""}`);
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setPushingAll(false); }
  };

  const exportLeads = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/client/projects/${pid}/leads/export?fmt=${fmt}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : `leads.${fmt}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${sorted.length} lead${sorted.length === 1 ? "" : "s"} (${fmt.toUpperCase()})`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setExporting(null);
    }
  };

  const HeaderControls = (
    <div className="flex items-center gap-2 flex-wrap">
      {crmReady && (
        <button
          onClick={pushAll}
          disabled={pushingAll || sorted.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs disabled:opacity-50 hover:bg-emerald-700"
          data-testid="client-leads-push-all"
        >
          {pushingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Send all to CRM
        </button>
      )}
      <button
        onClick={() => setShowCrm((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-xs hover:bg-zinc-50"
        data-testid="client-leads-crm-toggle"
      >
        <Plug className="h-3.5 w-3.5" />
        CRM settings{crmReady ? ` · ${crm.provider === "pipedrive" ? "Pipedrive" : "HubSpot"}` : ""}
      </button>
      <button
        onClick={() => exportLeads("xlsx")}
        disabled={!!exporting || sorted.length === 0}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-xs disabled:opacity-50 hover:bg-zinc-800"
        data-testid="client-leads-export-xlsx"
      >
        {exporting === "xlsx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        Export to Excel
      </button>
      <button
        onClick={() => exportLeads("csv")}
        disabled={!!exporting || sorted.length === 0}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-xs disabled:opacity-50 hover:bg-zinc-50"
        data-testid="client-leads-export-csv"
      >
        {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        CSV
      </button>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="client-leads-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-zinc-500">
          {sorted.length} lead{sorted.length === 1 ? "" : "s"}{sorted.length ? " · click a row for full details" : ""}
        </div>
        {HeaderControls}
      </div>

      {showCrm && <CrmSettingsPanel crm={crm} onSaved={(c) => { setCrm(c); }} />}

      {sorted.length === 0 ? (
        <div className="rounded-xl border border-zinc-200 bg-white px-5 py-14 text-center text-sm text-zinc-500" data-testid="client-leads-empty">
          No leads captured yet. They appear here the moment a buyer submits an enquiry from the public viewer.
        </div>
      ) : (
      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Contact</th>
              <th className="text-left px-5 py-3">Interested unit</th>
              <th className="text-left px-5 py-3">Signals</th>
              <th className="text-left px-5 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((l) => {
              const unit = l.unit_code || codeFor(l.entity_id) || "—";
              const signals = [
                l.shortlisted_codes?.length ? `${l.shortlisted_codes.length} shortlisted` : null,
                l.pdf_downloaded ? "PDF" : null,
                l.whatsapp_clicked ? "WhatsApp" : null,
                l.plan_opened ? "Plan" : null,
                l.gallery_opened ? "Gallery" : null,
                l.video_opened ? "Video" : null,
              ].filter(Boolean);
              return (
                <tr
                  key={l.id}
                  className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer align-top"
                  onClick={() => setOpenId(l.id)}
                  data-testid={`client-lead-row-${l.id}`}
                >
                  <td className="px-5 py-3 font-medium text-zinc-900">{l.name || "Anonymous"}</td>
                  <td className="px-5 py-3 text-zinc-600">
                    <div>{l.phone || "—"}</div>
                    {l.email && <div className="text-xs text-zinc-400">{l.email}</div>}
                  </td>
                  <td className="px-5 py-3 text-zinc-600 font-mono text-xs">{unit}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1 items-center">
                      {signals.length === 0 ? <span className="text-xs text-zinc-400">—</span> :
                        signals.map((s) => (
                          <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{s}</span>
                        ))}
                      {l.crm_pushed && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-0.5" data-testid={`client-lead-crm-badge-${l.id}`}>
                          <Check className="h-2.5 w-2.5" /> CRM
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-zinc-500 text-xs whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {openId && (() => {
        const l = sorted.find((x) => x.id === openId);
        if (!l) return null;
        const rows = [
          ["Name", l.name],
          ["Phone", l.phone],
          ["Email", l.email],
          ["Interested unit", l.unit_code || codeFor(l.entity_id)],
          ["Shortlisted", (l.shortlisted_codes || []).join(", ")],
          ["Compared", (l.compared_codes || []).join(", ")],
          ["Message", l.message],
          ["Language", l.language],
          ["Source", l.source],
          ["Source URL", l.source_url],
          ["Plan opened", l.plan_opened ? "Yes" : null],
          ["Gallery opened", l.gallery_opened ? "Yes" : null],
          ["Video opened", l.video_opened ? "Yes" : null],
          ["PDF downloaded", l.pdf_downloaded ? "Yes" : null],
          ["WhatsApp clicked", l.whatsapp_clicked ? "Yes" : null],
          ["Session", l.session_id],
          ["Submitted", l.created_at ? new Date(l.created_at).toLocaleString() : null],
        ].filter(([, v]) => v !== null && v !== undefined && v !== "");
        return (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpenId(null)}>
            <div className="bg-white w-full max-w-lg rounded-xl p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="client-lead-detail-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl">{l.name || "Lead details"}</h3>
                <button onClick={() => setOpenId(null)} className="p-2 hover:bg-zinc-100 rounded text-zinc-500" data-testid="client-lead-detail-close">✕</button>
              </div>
              <dl className="space-y-2.5">
                {rows.map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 gap-3 text-sm">
                    <dt className="text-zinc-500">{k}</dt>
                    <dd className="col-span-2 text-zinc-900 break-words">{String(v)}</dd>
                  </div>
                ))}
              </dl>
              {l.phone && (
                <div className="mt-5 flex gap-2">
                  <a href={`tel:${l.phone}`} className="flex-1 text-center px-4 py-2 rounded-md border border-zinc-300 text-sm hover:bg-zinc-50" data-testid="client-lead-call">Call</a>
                  <a href={`https://wa.me/${(l.phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex-1 text-center px-4 py-2 rounded-md bg-zinc-900 text-white text-sm" data-testid="client-lead-whatsapp">WhatsApp</a>
                </div>
              )}
              {crmReady && (
                <button
                  onClick={() => pushOne(l.id)}
                  disabled={pushingId === l.id}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-50 hover:bg-emerald-700"
                  data-testid="client-lead-push-crm"
                >
                  {pushingId === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {l.crm_pushed ? "Re-send to CRM" : `Send to ${crm.provider === "pipedrive" ? "Pipedrive" : "HubSpot"}`}
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}


// ── CRM settings panel (iter49) ────────────────────────────────────────────
// Each client configures its OWN CRM (Pipedrive or HubSpot) + API token. The
// token is write-only: the backend returns only a masked hint. Auto-push sends
// every new enquiry to the CRM the moment it arrives.
function CrmSettingsPanel({ crm, onSaved }) {
  const [provider, setProvider] = useState(crm?.provider || "");
  const [token, setToken] = useState("");
  const [domain, setDomain] = useState(crm?.pipedrive_domain || "");
  const [autoPush, setAutoPush] = useState(!!crm?.auto_push);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(
    crm?.last_test_status ? { ok: crm.last_test_status === "ok", detail: crm.last_test_detail } : null,
  );

  useEffect(() => {
    setProvider(crm?.provider || "");
    setDomain(crm?.pipedrive_domain || "");
    setAutoPush(!!crm?.auto_push);
  }, [crm?.provider, crm?.pipedrive_domain, crm?.auto_push]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { provider: provider || null, auto_push: autoPush, enabled: true };
      if (token) body.api_token = token;
      if (provider === "pipedrive") body.pipedrive_domain = domain;
      const r = await api.put("/client/crm-config", body);
      setToken("");
      onSaved?.(r.data);
      toast.success("CRM settings saved");
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/client/crm-config/test");
      setTestResult(r.data);
      if (r.data.ok) toast.success(r.data.detail); else toast.error(r.data.detail);
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setTesting(false); }
  };

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 space-y-4" data-testid="client-crm-settings">
      <div>
        <h3 className="text-sm font-medium text-zinc-900 inline-flex items-center gap-2"><Plug className="h-4 w-4 text-zinc-500" /> CRM integration</h3>
        <p className="text-xs text-zinc-500 mt-1">Connect your own CRM to receive enquiries automatically. Your API token is stored securely and never shown again.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-xs text-zinc-700 mb-1.5">CRM provider</div>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm bg-white"
            data-testid="crm-provider-select"
          >
            <option value="">None (export only)</option>
            <option value="pipedrive">Pipedrive</option>
            <option value="hubspot">HubSpot</option>
          </select>
        </label>
        {provider === "pipedrive" && (
          <label className="block">
            <div className="text-xs text-zinc-700 mb-1.5">Company domain</div>
            <div className="flex items-center gap-1">
              <input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="yourcompany"
                className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm"
                data-testid="crm-pipedrive-domain"
              />
              <span className="text-xs text-zinc-400 whitespace-nowrap">.pipedrive.com</span>
            </div>
          </label>
        )}
        {provider && (
          <label className="block md:col-span-2">
            <div className="text-xs text-zinc-700 mb-1.5">
              API token {crm?.api_token_set && <span className="text-zinc-400">(saved: {crm.api_token_hint} — leave blank to keep)</span>}
            </div>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={provider === "pipedrive" ? "Settings → Personal preferences → API" : "Settings → Integrations → Private Apps → token"}
              className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm"
              data-testid="crm-api-token"
            />
          </label>
        )}
      </div>
      {provider && (
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input type="checkbox" checked={autoPush} onChange={(e) => setAutoPush(e.target.checked)} data-testid="crm-auto-push" />
          Automatically send every new enquiry to {provider === "pipedrive" ? "Pipedrive" : "HubSpot"}
        </label>
      )}
      {testResult && (
        <div className={`text-xs rounded-md px-3 py-2 ${testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="crm-test-result">
          {testResult.detail}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm disabled:opacity-50" data-testid="crm-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
        {provider && crm?.api_token_set && (
          <button onClick={test} disabled={testing} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-zinc-300 text-sm disabled:opacity-50" data-testid="crm-test">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Test connection
          </button>
        )}
      </div>
    </div>
  );
}
